import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./Landing.css";
import CookieConsent from "./CookieConsent";
gsap.registerPlugin(ScrollTrigger);

// ─── Timeline config ──────────────────────────────────────────────────────────
const TIMELINE_TABS = ["Stop Losing", "Capture Leads", "Grow Revenue"];
const SECTION_IDS = ["#stats", "#features", "#pricing"];
const easeSnappy = "cubic-bezier(0.87, 0, 0.13, 1)";

// ─── Injected styles ──────────────────────────────────────────────────────────
const PILL_STYLES = `
  /* pill wrapper — fixed bottom-centre; GSAP will translateY it to screen-centre */
  .gerbo-pill-wrapper {
    position: fixed;
    bottom: 1.25rem;
    left: 0;
    right: 0;
    z-index: 10001;
    display: flex;
    justify-content: center;
    pointer-events: none;
  }

  .gerbo-timeline-pill {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    height: 2.35rem;
    padding: 0.21rem;
    background: hsla(0,0%,100%,0.95);
    border-radius: 3px;
    box-shadow: 0 0 5.2px 0 rgb(0 0 0/8%), 0 3.335px 3.335px 0 rgb(0 0 0/7%);
    position: relative;
  }
  .gerbo-timeline-pill.is-visible {
    pointer-events: auto;
  }

  /* the sliding teal bar */
  .gerbo-timeline-bar {
    position: absolute;
    top: 0.21rem;
    left: 0.21rem;
    width: 7rem;
    height: calc(100% - 0.42rem);
    background: #0d9488;
    border-radius: 2px;
    z-index: 0;
    pointer-events: none;
    will-change: transform, width;
  }

  /* individual tab buttons */
  .gerbo-timeline-item {
    position: relative;
    flex-shrink: 0;
    padding: 0.72rem 0;
    width: 7rem;
    font-size: 0.6rem;
    font-weight: 600;
    line-height: 100%;
    color: #0d9488;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.06rem;
    cursor: pointer;
    background: none;
    border: none;
    z-index: 10002;
    transition: color 0.33s ease;
  }
  .gerbo-timeline-item[data-active="true"] { color: #fff; }

  /* larger pill when centred */
  .gerbo-timeline-pill.is-centred {
    height: 3rem;
    box-shadow: 0 8px 40px rgba(0,0,0,0.35);
  }
  .gerbo-timeline-pill.is-centred .gerbo-timeline-item {
    width: 8.5rem;
    font-size: 0.65rem;
  }

  /* full-screen reveal overlay — sits BEHIND the pill (z 9998) */
  .gerbo-footer-reveal {
    position: fixed;
    inset: 0;
    z-index: 9998;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
  }
  .gerbo-footer-reveal-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  /* dark scrim keeps pill legible on any image */
  .gerbo-footer-reveal-scrim {
    position: absolute;
    inset: 0;
    background: rgba(0,0,0,0.6);
  }
      .gerbo-sentinel {
    height: 100vh;
    background: transparent;
    pointer-events: none;
    position: relative;
  }
`;

export const Landing: React.FC = () => {
  const navigate = useNavigate();

  // ── State ───────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);
  const activeTabRef = useRef(0);
  activeTabRef.current = activeTab;

  // ── Refs ────────────────────────────────────────────────────────────────────
  const pillWrapperRef = useRef<HTMLDivElement>(null);
  const navPillRef = useRef<HTMLDivElement>(null);
  const navBarRef = useRef<HTMLDivElement>(null);
  const revealRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const posCache = useRef<Map<number, { x: number; width: number }>>(new Map());
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isCentred = useRef(false);

  // ── Bar position helpers ────────────────────────────────────────────────────
  const getBarPos = useCallback((index: number) => {
    if (posCache.current.has(index)) return posCache.current.get(index)!;
    const pill = navPillRef.current;
    const item = itemRefs.current[index];
    if (!pill || !item) return null;
    const pillRect = pill.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const pos = { x: itemRect.left - pillRect.left, width: itemRect.width };
    posCache.current.set(index, pos);
    return pos;
  }, []);

  const setActiveBar = useCallback(
    (index: number) => {
      const pos = getBarPos(index);
      if (!pos || !navBarRef.current) return;
      gsap.set(navBarRef.current, { x: pos.x, width: pos.width });
      itemRefs.current.forEach((el, i) => {
        if (el) el.dataset.active = i === index ? "true" : "false";
      });
    },
    [getBarPos],
  );

  const setHoverBar = useCallback(
    (index: number) => {
      const pos = getBarPos(index);
      if (!pos || !navBarRef.current) return;
      gsap.to(navBarRef.current, {
        x: pos.x,
        width: pos.width,
        duration: 0.33,
        ease: "power2.inOut",
      });
    },
    [getBarPos],
  );

  const clearHoverBar = useCallback(() => {
    const pos = getBarPos(activeTabRef.current);
    if (!pos || !navBarRef.current) return;
    gsap.to(navBarRef.current, {
      x: pos.x,
      width: pos.width,
      duration: 0.33,
      ease: "power2.inOut",
    });
  }, [getBarPos]);

  // ── Sync bar on tab change ──────────────────────────────────────────────────
  useEffect(() => {
    const id = requestAnimationFrame(() => setActiveBar(activeTab));
    return () => cancelAnimationFrame(id);
  }, [activeTab, setActiveBar]);

  // ── Bust pos-cache on resize ────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => {
      posCache.current.clear();
      setActiveBar(activeTabRef.current);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [setActiveBar]);

  // ── Hero clip-path + initial pill reveal (fires 2.5 s after mount) ─────────
  useEffect(() => {
    const pill = navPillRef.current;
    const wrapper = pillWrapperRef.current;
    if (!pill || !wrapper) return;

    gsap.set("#hero-section", { clipPath: "inset(0 0 0% 0)" });
    gsap.set(pill, { autoAlpha: 0, y: 20 });

    const timer = setTimeout(() => {
      gsap.to("#hero-section", {
        clipPath: "inset(0 0 8% 0)",
        duration: 1.4,
        ease: "power3.inOut",
      });
      gsap.to(pill, {
        autoAlpha: 1,
        y: 0,
        duration: 0.7,
        delay: 0.2,
        ease: easeSnappy,
        onStart: () => pill.classList.add("is-visible"),
      });
    }, 2500);

    return () => clearTimeout(timer);
  }, []);

  // ── Footer-end reveal ───────────────────────────────────────────────────────
  //
  //  When the bottom of the footer touches the bottom of the viewport
  //  (user has scrolled all the way down):
  //    1. fade in the full-screen background image
  //    2. animate the pill wrapper from its normal bottom position up to
  //       the vertical centre of the screen
  //
  //  Scrolling back up reverses both effects.
  //
  useEffect(() => {
    const footer = document.querySelector(
      "footer.footer",
    ) as HTMLElement | null;
    const pill = navPillRef.current;
    const wrapper = pillWrapperRef.current;
    const reveal = revealRef.current;
    if (!footer || !pill || !wrapper || !reveal) return;

    const animateToCenter = () => {
      if (isCentred.current) return;
      isCentred.current = true;

      // 1. show the background image
      gsap.to(reveal, { autoAlpha: 1, duration: 0.8, ease: "power2.inOut" });

      // 2. grow the pill
      pill.classList.add("is-centred");
      posCache.current.clear();

      // 3. move wrapper to vertical centre
      //    The wrapper sits at bottom:1.25rem.  We need to translate it
      //    upward by (vh/2 - pillCentreFromBottom).
      //    pillCentreFromBottom ≈ 1.25rem + pillHeight/2
      //    pillHeight when centred ≈ 48 px,  1.25rem ≈ 20px
      const vh = window.innerHeight;
      const pillH = 48;
      const fromBottom = 20 + pillH / 2; // px from viewport bottom to pill centre
      const toBottom = vh / 2; // px from viewport bottom to screen centre
      const deltaY = -(toBottom - fromBottom); // negative = upward

      gsap.to(wrapper, { y: deltaY, duration: 1, ease: "power3.inOut" });

      // re-sync the sliding bar after class change
      setTimeout(() => setActiveBar(activeTabRef.current), 60);
    };

    const animateToBottom = () => {
      if (!isCentred.current) return;
      isCentred.current = false;

      gsap.to(reveal, { autoAlpha: 0, duration: 0.5, ease: "power2.inOut" });
      gsap.to(wrapper, { y: 0, duration: 0.8, ease: "power3.inOut" });

      pill.classList.remove("is-centred");
      posCache.current.clear();
      setTimeout(() => setActiveBar(activeTabRef.current), 60);
    };

    const st = ScrollTrigger.create({
      trigger: footer,
      start: "bottom bottom", // footer bottom reaches viewport bottom
      onEnter: () => animateToCenter(),
      onLeaveBack: () => animateToBottom(),
    });

    return () => st.kill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── ScrollTrigger: update active tab while scrolling ───────────────────────
  useEffect(() => {
    const triggers = SECTION_IDS.map((sel, i) => {
      const el = document.querySelector(sel);
      if (!el) return null;
      return ScrollTrigger.create({
        trigger: el,
        start: "top center",
        end: "bottom center",
        onEnter: () => setActiveTab(i),
        onEnterBack: () => setActiveTab(i),
      });
    });
    return () => triggers.forEach((t) => t?.kill());
  }, []);

  // ── Tab click ──────────────────────────────────────────────────────────────
  const handleTabClick = (index: number) => {
    setActiveTab(index);
    const el = document.querySelector(SECTION_IDS[index]);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // ── AOS observer ──────────────────────────────────────────────────────────
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("aos-animate");
        }),
      { threshold: 0.1, rootMargin: "0px 0px -100px 0px" },
    );
    document
      .querySelectorAll(
        ".stat-card,.feature-card,.step,.industry-card,.pricing-card,.testimonial-card,.automation-card",
      )
      .forEach((el) => {
        el.setAttribute("data-aos", "fade-up");
        observer.observe(el);
      });
    return () => observer.disconnect();
  }, []);

  const toggleMobileMenu = () => {
    const navMenu = document.querySelector(".nav-menu") as HTMLElement;
    if (navMenu.style.display === "flex") {
      navMenu.style.display = "none";
    } else {
      Object.assign(navMenu.style, {
        display: "flex",
        flexDirection: "column",
        position: "absolute",
        top: "100%",
        left: "0",
        right: "0",
        background: "rgba(10,14,39,0.95)",
        padding: "2rem",
        boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
        zIndex: "1000",
      });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: PILL_STYLES }} />

      {/* ── FULL-SCREEN REVEAL OVERLAY ─────────────────────────────────────────
          Swap the src for any image you want shown at scroll-end.
          Recommended: a high-quality photo of your team, office, or product.
          The dark scrim keeps the pill readable regardless of image brightness.
      ── */}
      <div ref={revealRef} className="gerbo-footer-reveal">
        <img
          src="/images/gerbo-space.jpg"
          alt="Gerbo end-of-page reveal"
          className="gerbo-footer-reveal-img"
        />
        <div className="gerbo-footer-reveal-scrim" />
      </div>

      {/* ── TIMELINE PILL ─────────────────────────────────────────────────────
          Fixed bottom-centre; GSAP translates it to vertical centre when the
          footer-end ScrollTrigger fires.
      ── */}
      <div ref={pillWrapperRef} className="gerbo-pill-wrapper">
        <div ref={navPillRef} className="gerbo-timeline-pill">
          <div ref={navBarRef} className="gerbo-timeline-bar" />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            {TIMELINE_TABS.map((tab, i) => (
              <button
                key={tab}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                data-active={activeTab === i}
                className="gerbo-timeline-item"
                onClick={() => handleTabClick(i)}
                onMouseEnter={() => {
                  if (hoverTimer.current) {
                    clearTimeout(hoverTimer.current);
                    hoverTimer.current = null;
                  }
                  if (i !== activeTab) setHoverBar(i);
                }}
                onMouseLeave={() => {
                  hoverTimer.current = setTimeout(clearHoverBar, 50);
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── HEADER ── */}
      <header className="header">
        <nav className="nav-container">
          <a className="logo" href="/">
            <img
              src="/images/k0PQG0X6hhyOXUOKss86eJY14-2.png"
              alt="Gerbo Logo"
            />
          </a>
          <button
            className="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={toggleMobileMenu}
          >
            ☰
          </button>
          <ul className="nav-menu">
            <li>
              <a href="#features">Features</a>
            </li>
            <li>
              <a href="#automation">Automation</a>
            </li>
            <li>
              <a href="#pricing">Pricing</a>
            </li>
            <li>
              <a href="#industries">Industries</a>
            </li>
            <li>
              <a href="#faqs">FAQ</a>
            </li>
            <li>
              <a href="/contact">Contact</a>
            </li>
            <li>
              <a href="/blog">Blog</a>
            </li>
            <li>
              <button
                onClick={() => navigate("/login")}
                className="btn btn-primary"
              >
                Try Gerbo Live
              </button>
            </li>
          </ul>
        </nav>
      </header>

      {/* ── HERO ── */}
      <section className="hero" id="hero-section">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="hero-video"
          src="/videos/phone.mp4"
        />
        <div className="hero-overlay" />
        <div className="hero-content">
          <img
            src="/images/8EYtClIJaotk40RaLdi5bBD444-1.png"
            alt=""
            className="hero-logo"
          />
          <div className="hero-badge">Trusted by 150+ SA Businesses</div>
          <h1>
            Never Miss a Call. <span className="highlight">Gerbo</span> answers
            for you.
          </h1>
          <p>
            Your friendly AI receptionist with a natural voice. Available 24/7
            to greet callers, capture details, and send summaries straight to
            your inbox — and now able to control your home or office along the
            way.
          </p>
          <div className="hero-cta">
            <button
              onClick={() => navigate("/register")}
              className="btn btn-primary"
            >
              Try Gerbo Live Now
            </button>
            <a href="#pricing" className="btn btn-secondary-white">
              View Pricing
            </a>
          </div>
        </div>
      </section>

      {/* ── TRUST ── */}
      <section className="trust-section">
        <div className="trust-container">
          <p className="trust-text">Trusted by</p>
          <h2 className="trust-count">150+</h2>
          <p className="trust-text">SA businesses</p>
        </div>
      </section>

      {/* ── STATS  (tab 0 — "Stop Losing") ── */}
      <section className="stats-section" id="stats">
        <div className="section-container">
          <h2 className="section-title">The Cost of Missed Calls</h2>
          <p className="section-subtitle">
            <strong>80% of missed calls never ring again.</strong> This means
            you could be losing <strong>$7,500</strong> a year to missed
            opportunities.
          </p>
          <div className="stats-grid">
            <div className="stat-card">
              <h3>💰 Hidden Revenue Loss</h3>
              <p>
                Lost revenue adds up fast. With Gerbo answering, you can
                steadily recapture that money, turning missed opportunities into
                real growth.
              </p>
            </div>
            <div className="stat-card">
              <h3>📈 Start Capturing More</h3>
              <p>
                Don't let this happen to your business. Gerbo answers every call
                so you never miss a lead.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES  (tab 1 — "Capture Leads") ── */}
      <section className="features-section" id="features">
        <div className="section-container">
          <h2 className="section-title">Why Businesses Choose Gerbo</h2>
          <p className="section-subtitle">
            Effortless deployment with unmatched professionalism and peace of
            mind.
          </p>
          <div className="features-grid">
            {[
              {
                icon: "⏰",
                t: "24/7 Availability",
                d: "Never miss a call again. Gerbo captures missed leads while you're busy or closed.",
              },
              {
                icon: "📝",
                t: "Captures Every Detail",
                d: "Names, reasons, and contact info — Gerbo never forgets a single detail.",
              },
              {
                icon: "⚡",
                t: "Saves Your Time",
                d: "No voicemails, no chasing, no interruptions. Just productive work.",
              },
              {
                icon: "🎙️",
                t: "Feels Human, Not Robotic",
                d: "Your callers feel heard and valued, not fobbed off by a machine.",
              },
              {
                icon: "📈",
                t: "Increases Revenue",
                d: "Gerbo ensures you never lose another potential customer or lead.",
              },
              {
                icon: "🔒",
                t: "Complete Peace of Mind",
                d: "Gerbo works even when you can't. Always notified. Always covered.",
              },
              {
                icon: "🏠",
                t: "Home & Office Automation",
                d: "Let Gerbo securely open gates, unlock doors, and control lights or alarms for verified callers — all from the same phone line.",
              },
            ].map((f) => (
              <div className="feature-card" key={f.t}>
                <div className="feature-icon">{f.icon}</div>
                <h3>{f.t}</h3>
                <p>{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="how-it-works" id="how-it-works">
        <div className="section-container">
          <h2 className="section-title">How Gerbo Works</h2>
          <p className="section-subtitle">
            3 simple steps to turn missed calls into new clients.
          </p>
          <div className="steps-container">
            {[
              {
                n: 1,
                t: "Divert Your Calls to Gerbo",
                d: "No complicated setup or technical knowledge required. We offer stress-free onboarding.",
              },
              {
                n: 2,
                t: "Gerbo Greets & Helps Callers",
                d: "Answers questions professionally, collects details, and represents your brand perfectly.",
              },
              {
                n: 3,
                t: "Get Instant Summaries",
                d: "Receive call details by email or SMS, so you can follow up at your convenience.",
              },
            ].map((s) => (
              <div className="step" key={s.n}>
                <div className="step-number">{s.n}</div>
                <div className="step-content">
                  <h3>{s.t}</h3>
                  <p>{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AUTOMATION  (Home & Office Automation add-on) ── */}
      <section className="automation-section" id="automation">
        <div className="section-container">
          <h2 className="section-title">
            Beyond Answering: Home & Office Automation
          </h2>
          <p className="section-subtitle">
            Gerbo doesn't just take a message — for verified callers, it can
            control your space in real time. A delivery arrives, a client shows
            up early, or a contractor needs access: Gerbo handles it, securely
            and on your terms.
          </p>
          <div className="features-grid">
            {[
              {
                icon: "🚪",
                t: "Gate & Door Access",
                d: "Verified callers can be let in hands-free — perfect for deliveries, staff, or expected visitors.",
              },
              {
                icon: "💡",
                t: "Lights & Climate",
                d: "Ask Gerbo to switch on lights or adjust the thermostat ahead of arrival, right from a phone call.",
              },
              {
                icon: "🚨",
                t: "Alarm Awareness",
                d: "Gerbo can check and report alarm or security status, and alert you the moment something needs attention.",
              },
              {
                icon: "🔐",
                t: "PIN-Verified Actions",
                d: "Sensitive actions require a PIN or whitelisted caller ID, with every action logged and time-stamped.",
              },
            ].map((f) => (
              <div className="feature-card automation-card" key={f.t}>
                <div className="feature-icon">{f.icon}</div>
                <h3>{f.t}</h3>
                <p>{f.d}</p>
              </div>
            ))}
          </div>
          <p
            style={{
              textAlign: "center",
              marginTop: "2.5rem",
              color: "var(--text-light)",
              fontSize: "1.05rem",
            }}
          >
            Available as a premium add-on on the Elite plan, built on your
            existing smart home or office system.
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "1.5rem",
            }}
          >
            <a href="/contact" className="btn btn-primary">
              Ask About Automation
            </a>
          </div>
        </div>
      </section>

      {/* ── INDUSTRIES ── */}
      <section className="industries-section" id="industries">
        <div className="section-container">
          <h2 className="section-title">Industries We Serve</h2>
          <p className="section-subtitle">
            Gerbo is trusted by professionals across the SADC to answer calls,
            capture leads, and keep business moving.
          </p>
          <div className="industries-grid">
            {[
              {
                icon: "🏘️",
                t: "Estate Agents",
                d: "Capture every viewing request, even after office hours.",
              },
              {
                icon: "🦷",
                t: "Healthcare & Dentists",
                d: "Never miss a patient inquiry or appointment request.",
              },
              {
                icon: "🍽️",
                t: "Restaurants & Bars",
                d: "Handle booking enquiries any time, day or night.",
              },
              {
                icon: "🔧",
                t: "Tradesmen & Contractors",
                d: "Capture jobs while you're on site or driving between locations.",
              },
              {
                icon: "💅",
                t: "Salons & Spas",
                d: "Secure more bookings while you're with clients.",
              },
              {
                icon: "📊",
                t: "Accountants",
                d: "Never miss a client inquiry during busy tax season or while you're in consultations.",
              },
              {
                icon: "🛡️",
                t: "Security Companies",
                d: "Ensure every call is answered promptly, day or night, so no incident goes unreported.",
              },
              {
                icon: "🪑",
                t: "Upholstery",
                d: "Capture every quote request and booking while you're hands-on with a job.",
              },
              {
                icon: "🏠",
                t: "Smart Homes & Offices",
                d: "Let trusted callers unlock gates, adjust lights, or check alarm status hands-free.",
              },
            ].map((c) => (
              <div className="industry-card" key={c.t}>
                <h4>
                  {c.icon} {c.t}
                </h4>
                <p>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPARISON ── */}
      <section className="comparison-section" id="comparison">
        <div className="section-container">
          <h2 className="section-title">See the Difference</h2>
          <p className="section-subtitle">
            Your business with Gerbo... and without it.
          </p>
          <div className="comparison-grid">
            <div className="comparison-card with-gerbo">
              <div className="comparison-header">
                <h3>✅ With Gerbo</h3>
                <p>The Smart Choice</p>
              </div>
              <ul className="comparison-list">
                {[
                  "100% of Calls Answered, 24/7",
                  "More Leads Captured = More Sales",
                  "Happy Clients, Loyal Customers",
                  "Always-On Coverage After Hours",
                  "Instant Call Summaries to Email",
                  "GDPR Compliant & SADC-Based",
                  "Optional Home & Office Automation",
                ].map((t) => (
                  <li key={t}>
                    <span className="comparison-icon check">✓</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="comparison-card without-gerbo">
              <div className="comparison-header">
                <h3>❌ Without Gerbo</h3>
                <p>The Risks</p>
              </div>
              <ul className="comparison-list">
                {[
                  "80% of Missed Calls Never Return",
                  "$7,500+ Lost Revenue Annually",
                  "Frustrated & Lost Clients",
                  "No After-Hours Coverage",
                  "Scrambling to Follow Up",
                  "Risk of GDPR Issues",
                  "No Remote Access Control",
                ].map((t) => (
                  <li key={t}>
                    <span className="comparison-icon cross">✗</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING  (tab 2 — "Grow Revenue") ── */}
      <section className="pricing-section" id="pricing">
        <div className="section-container">
          <h2 className="section-title">Flexible Plans for Every Business</h2>
          <p className="section-subtitle">
            Choose a plan that fits your goals and scale as you grow
          </p>
          <div className="pricing-grid">
            {[
              {
                name: "Essential",
                price: "$29",
                popular: false,
                cta: "Start Essential",
                features: [
                  "75 Calls / Month Included",
                  "AI Answering with Natural Voice",
                  "Instant Call Summaries by Email",
                  "GDPR Compliant",
                  "24/7 Call Answering",
                ],
              },
              {
                name: "Pro",
                price: "$49",
                popular: true,
                cta: "Choose Pro",
                features: [
                  "200 Calls / Month Included",
                  "Everything in Essential",
                  "Priority Support",
                  "Best Value for Growing Businesses",
                ],
              },
              {
                name: "Elite",
                price: "$89",
                popular: false,
                cta: "Contact Sales",
                features: [
                  "1,000 Calls / Month Included",
                  "Everything in Pro",
                  "Early Access to New Features",
                  "Dedicated Account Manager",
                  "Priority Onboarding & Support",
                  "Custom Features (SMS, CRM)",
                  "Home & Office Automation Add-On",
                ],
              },
            ].map((p) => (
              <div
                className={`pricing-card${p.popular ? " popular" : ""}`}
                key={p.name}
              >
                {p.popular && (
                  <span className="popular-badge">Most Popular</span>
                )}
                <div className="pricing-header">
                  <h3 className="pricing-name">{p.name}</h3>
                  <div className="pricing-price">{p.price}</div>
                  <p className="pricing-period">/month</p>
                </div>
                <ul className="pricing-features">
                  {p.features.map((f) => (
                    <li key={f}>
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="/contact"
                  className="btn btn-primary"
                  style={{ width: "100%" }}
                >
                  {p.cta}
                </a>
              </div>
            ))}
          </div>
          <p
            style={{
              textAlign: "center",
              marginTop: "3rem",
              color: "var(--text-light)",
              fontSize: "1.2rem",
              fontWeight: 600,
            }}
          >
            From just $1.95/day — Never Miss a Lead Again!
          </p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="logo footer-column">
              <img
                src="/images/8EYtClIJaotk40RaLdi5bBD444-1.png"
                alt="Gerbo Logo"
                style={{ marginBottom: "1.5rem" }}
              />
              <p
                style={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: "1rem",
                  lineHeight: 1.7,
                }}
              >
                Your friendly AI receptionist, always available to help your
                business grow.
              </p>
            </div>
            <div className="footer-column">
              <h4>Product</h4>
              <ul className="footer-links">
                <li>
                  <a href="#features">Features</a>
                </li>
                <li>
                  <a href="#automation">Automation</a>
                </li>
                <li>
                  <a href="#pricing">Pricing</a>
                </li>
                <li>
                  <a href="#industries">Industries</a>
                </li>
                <li>
                  <a href="/blog">Blog</a>
                </li>
              </ul>
            </div>
            <div className="footer-column">
              <h4>Company</h4>
              <ul className="footer-links">
                <li>
                  <a href="/contact">Contact</a>
                </li>
                <li>
                  <a href="/privacy">Privacy</a>
                </li>
                <li>
                  <a href="/terms">Terms</a>
                </li>
              </ul>
            </div>
            <div className="footer-column">
              <h4>Support</h4>
              <ul className="footer-links">
                <li>
                  <a href="mailto:support@gerboai.com">support@gerboai.com</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2025 Gerbo. All rights reserved.</p>
            <p>Made by Solid5</p>
          </div>
        </div>
      </footer>
      <CookieConsent />
      <div ref={sentinelRef} className="gerbo-sentinel" aria-hidden="true" />
      {/* ── VAPI WIDGET ── */}
      <div
        dangerouslySetInnerHTML={{
          __html: `
        <vapi-widget
          public-key="6fbcc6b2-c19e-4f0a-b3fb-dac4a5a4f377"
          assistant-id="0ef8c3c9-a7c1-4b33-bef2-374ca299ffc0"
          mode="chat" theme="dark"
          base-bg-color="#000000" accent-color="#14B8A6"
          cta-button-color="#000000" cta-button-text-color="#ffffff"
          border-radius="large" size="full" position="bottom-right"
          start-button-text="Start" end-button-text="End Call"
          chat-first-message="Hey, How can I help you today?"
          chat-placeholder="Type your message..."
          voice-show-transcript="true" consent-required="false"
        ></vapi-widget>
      `,
        }}
      />
    </>
  );
};
